// localDb.js — PGlite singleton with schema migrations.
//
// PGlite is a full Postgres engine running in WebAssembly. Two backends:
//   - Browser: persisted to IndexedDB under 'idb://chatrone-os-v1'.
//   - Desktop shell: hosted on a real on-disk data dir by the Electron main
//     process (worker_thread), reached via the window.chatroneDesktop.db
//     bridge. Same schema/migrations/sync — this module owns them for both,
//     so web deploys can evolve the schema without a shell rebuild.
// The sync engine keeps either mirror current with Supabase in the background.
//
// Usage:
//   import { initLocalDb, getLocalDb, queryLocal, execLocal } from './localDb'
//
//   await initLocalDb()           // called once in main.jsx before render
//   const db = getLocalDb()       // returns the PGlite instance (or IPC proxy)
//   const { rows } = await queryLocal('SELECT _data FROM contacts WHERE id = $1', [id])

import { PGlite } from '@electric-sql/pglite'
import { SYNC_TABLES } from './syncConfig'

// ─── Schema version — bump when migration_vN is added ────────────────────────
const SCHEMA_VERSION = 3

let _db = null
let _initPromise = null

// Desktop-shell bridge to the on-disk PGlite (null in browsers).
const _bridge = () =>
  (typeof window !== 'undefined' && window.chatroneDesktop?.db) || null

// True when the local mirror is the desktop shell's on-disk DB. Used by the
// sync engine to gate local-first reads (disk is fast + durable; browser
// IndexedDB stays fallback-only).
export function isDesktopDb() {
  return !!_bridge()
}

// ─── Public API ───────────────────────────────────────────────────────────────

export async function initLocalDb() {
  if (_initPromise) return _initPromise
  _initPromise = _init()
  return _initPromise
}

export function getLocalDb() {
  return _db
}

// Convenience wrappers so callers don't import _db directly.
export async function queryLocal(sql, params = []) {
  if (!_db) throw new Error('localDb not initialized')
  return _db.query(sql, params)
}

export async function execLocal(sql) {
  if (!_db) throw new Error('localDb not initialized')
  return _db.exec(sql)
}

export function isLocalDbReady() {
  return _db !== null
}

// ─── Init ─────────────────────────────────────────────────────────────────────

// Threshold-triggered compaction. Plain VACUUM (hourly, see vacuumLocal) marks
// dead-tuple space reusable but NEVER shrinks the files PGlite persists to
// IndexedDB — the store only ratchets up to its churn high-water mark, and
// PGlite reads the whole thing into WASM memory on every boot. VACUUM FULL
// rewrites every table into fresh, tightly-packed files and drops the bloated
// originals. The original fix here was a one-time localStorage flag; the store
// re-bloated to ~1.2 GB within two days and nothing could ever compact it
// again. Now compaction fires whenever the measured database size crosses
// COMPACT_THRESHOLD_BYTES — self-healing regardless of what causes future
// growth. It runs on the normally-open connection (no cross-tab exclusivity
// needed — a Chatrone tab is almost always open, so an IDB delete would block
// forever), and is throttled via a shared localStorage stamp so multiple tabs
// don't stampede. The trailing CHECKPOINT lets Postgres start trimming the WAL
// segments that pile up under churn (~500 MB observed at stock max_wal_size).
const COMPACT_THRESHOLD_BYTES = 300 * 1024 * 1024   // healthy live set is well under 150 MB
const COMPACT_MIN_INTERVAL_MS = 6 * 60 * 60 * 1000  // at most one VACUUM FULL per 6 h per browser
const COMPACT_STAMP_KEY = 'chatrone_pglite_compact_last'

export async function maybeCompactLocal() {
  if (!_db) return false
  try {
    const last = parseInt(localStorage.getItem(COMPACT_STAMP_KEY) || '0', 10)
    if (Date.now() - last < COMPACT_MIN_INTERVAL_MS) return false
  } catch {
    return false // localStorage blocked (private mode) — skip
  }
  try {
    const { rows } = await _db.query('SELECT pg_database_size(current_database()) AS bytes')
    const before = Number(rows[0]?.bytes ?? 0)
    if (!before || before < COMPACT_THRESHOLD_BYTES) return false
    // Stamp BEFORE the (slow) rewrite so concurrently-booting tabs skip out.
    // On failure the stamp just delays the retry to the next 6 h window.
    try { localStorage.setItem(COMPACT_STAMP_KEY, String(Date.now())) } catch { /* ignore */ }
    console.info(`[localDb] store at ${Math.round(before / 1048576)} MB (threshold ${Math.round(COMPACT_THRESHOLD_BYTES / 1048576)} MB) — compacting (VACUUM FULL)…`)
    const t0 = Date.now()
    await _db.exec('VACUUM FULL')
    try { await _db.exec('CHECKPOINT') } catch { /* best effort */ }
    const { rows: afterRows } = await _db.query('SELECT pg_database_size(current_database()) AS bytes')
    const after = Number(afterRows[0]?.bytes ?? 0)
    console.info(`[localDb] compaction: ${Math.round(before / 1048576)} MB → ${Math.round(after / 1048576)} MB in ${Date.now() - t0}ms`)
    return true
  } catch (err) {
    console.warn('[localDb] compaction failed:', err?.message)
    return false
  }
}

// Self-recovery from an unbootable store. Observed 2026-07-08: after the store
// bloated past ~1 GB, PGlite began failing waitReady with a WASM "Aborted()"
// (out of memory loading the whole data dir, and/or state torn by a reload that
// killed a VACUUM mid-flush). Postgres-level recovery is impossible when init
// itself dies — and without init nothing can ever compact the store, so every
// future boot fails too. The mirror is only a cache of Supabase, so: a failed
// init schedules a reset; the NEXT boot deletes the IndexedDB database before
// creating PGlite and lets the sync engine rebuild from scratch (the age-capped
// working set, tens of MB). deleteDatabase succeeds only when NO other tab
// holds the store open (onblocked otherwise), so a transient failure in one tab
// never nukes a store that healthy tabs are still using — at worst the queued
// delete completes after every connection closes, and the next boot starts
// fresh. Cost when it fires: one full re-pull, plus any offline mutations still
// in _mutation_queue — which an unbootable store could never deliver anyway.
const RESET_FLAG = 'chatrone_pglite_reset_pending'

function _deleteBrokenStore() {
  return new Promise((resolve) => {
    let settled = false
    const done = (outcome) => { if (!settled) { settled = true; resolve(outcome) } }
    try {
      const req = indexedDB.deleteDatabase('/pglite/chatrone-os-v1')
      req.onsuccess = () => done('deleted')
      req.onerror   = () => done(`error: ${req.error?.message}`)
      req.onblocked = () => done('blocked — another tab holds the store; delete will complete when it closes')
      setTimeout(() => done('timeout'), 12_000)
    } catch (err) {
      done(`error: ${err?.message}`)
    }
  })
}

async function _init() {
  const bridge = _bridge()
  return bridge ? _initDesktop(bridge) : _initBrowser()
}

// ─── Desktop backend — on-disk PGlite behind the shell's IPC bridge ──────────
//
// NodeFS pages from a real filesystem: no whole-store-into-WASM boot, no
// IndexedDB quota/eviction, none of the browser self-heal machinery below.
// Recovery is simpler too: the store is a disposable cache, and bridge.reset()
// (rm dataDir + respawn worker) is safe mid-session because nothing else holds
// the directory — so a failed init resets inline and retries once instead of
// deferring to the next boot.

async function _initDesktop(bridge) {
  const proxy = {
    query: (sql, params = []) => bridge.query(sql, params),
    exec: (sql) => bridge.exec(sql),
  }
  const migrate = () => Promise.race([
    _runMigrations(proxy),
    new Promise((_, reject) => setTimeout(
      () => reject(new Error('desktop migration timeout (30s)')), 30_000)),
  ])
  try {
    try {
      await migrate()
    } catch (err) {
      console.warn('[localDb] desktop init failed — resetting on-disk store:', err?.message)
      await bridge.reset()
      await migrate()
    }
    // Same publish-after-migrations rule as the browser path (see _initBrowser).
    _db = proxy
    console.info('[localDb] ready (desktop/NodeFS) — schema v' + SCHEMA_VERSION)
  } catch (err) {
    console.error('[localDb] desktop init failed permanently:', err?.message)
    // Deliberately DON'T null _initPromise (unlike the browser path): callers
    // like localFirstRows await initLocalDb() per-fetch, and re-running init
    // here would rm-and-rebuild the store on every list mount. The cached
    // rejection makes them all fail fast until the next app launch.
    _db = null // callers detect unavailability, app degrades to Supabase-only
    throw err
  }
  return _db
}

// ─── Browser backend — IndexedDB-persisted PGlite (unchanged behavior) ───────

async function _initBrowser() {
  try {
    if (localStorage.getItem(RESET_FLAG)) {
      localStorage.removeItem(RESET_FLAG)  // one attempt per failed boot
      console.warn('[localDb] previous boot failed — deleting local store for a fresh rebuild…')
      console.warn(`[localDb] store deletion: ${await _deleteBrokenStore()}`)
    }
  } catch { /* localStorage blocked — proceed to normal init */ }
  try {
    // Bound the open+migrate. IndexedDB queues open() requests BEHIND a pending
    // deleteDatabase (which the reset path above can leave queued when another
    // tab holds the store), so init can HANG rather than fail — leaving the
    // mirror silently dead with _initPromise pending forever. A bounded failure
    // instead surfaces in the console, re-arms RESET_FLAG, and each subsequent
    // boot retries cleanly until the delete finally lands and a fresh store is
    // built. A very bloated store that can't finish loading in time gets the
    // same treatment — which is precisely the store we want reset anyway.
    const db = await (async () => {
      let timedOut = false
      const instance = new PGlite('idb://chatrone-os-v1')
      const ready = instance.waitReady.then(() => {
        // A late arrival after timeout is a zombie holding the IDB open (it
        // would block the very deleteDatabase the failure just scheduled).
        if (timedOut) instance.close().catch(() => {})
        return instance
      })
      return Promise.race([
        ready,
        new Promise((_, reject) => setTimeout(() => {
          timedOut = true
          reject(new Error('init timeout (90s) — store unreadable or a pending delete is blocking open'))
        }, 90_000)),
      ])
    })()
    await _runMigrations(db)
    // Publish the handle only AFTER migrations: getLocalDb() returning a
    // pre-migration db let the first syncAll() on a fresh profile race table
    // creation ("relation companies does not exist" on every cold boot).
    // Until this line, getLocalDb() is null and every caller skips cleanly.
    _db = db
    console.info('[localDb] ready — schema v' + SCHEMA_VERSION)
    // Retired one-time compaction flag (replaced by the threshold check that
    // runs after each hourly VACUUM in syncEngine) — clear so it can't confuse.
    try { localStorage.removeItem('chatrone_pglite_compacted_v1') } catch { /* ignore */ }
  } catch (err) {
    console.error('[localDb] init failed:', err?.message)
    // Schedule the fresh-rebuild path (see RESET_FLAG above) for the next boot.
    try { localStorage.setItem(RESET_FLAG, String(Date.now())) } catch { /* ignore */ }
    _db = null   // stay null so callers can detect unavailability
    _initPromise = null
    throw err
  }
  return _db
}

// Reclaim dead-tuple space so the persisted store stabilizes at working-set size
// instead of growing unbounded across repeated sync upserts. Regular VACUUM (not
// FULL) keeps freed space reusable in-place — cheap and non-locking. Throttled by
// the caller. Never throws; maintenance must not break sync.
export async function vacuumLocal() {
  if (!_db) return false
  try {
    await _db.exec('VACUUM')
    return true
  } catch (err) {
    console.warn('[localDb] VACUUM failed:', err?.message)
    return false
  }
}

// ─── Migration runner ─────────────────────────────────────────────────────────

async function _runMigrations(db) {
  // Infrastructure tables (idempotent — always created)
  await db.exec(`
    CREATE TABLE IF NOT EXISTS _meta (
      key   TEXT PRIMARY KEY,
      value TEXT NOT NULL
    );

    CREATE TABLE IF NOT EXISTS _sync_state (
      table_name     TEXT PRIMARY KEY,
      last_synced_at TIMESTAMPTZ,
      row_count      INTEGER DEFAULT 0,
      sync_error     TEXT
    );

    CREATE TABLE IF NOT EXISTS _mutation_queue (
      id           SERIAL PRIMARY KEY,
      table_name   TEXT        NOT NULL,
      operation    TEXT        NOT NULL,
      row_id       TEXT,
      payload      JSONB,
      created_at   TIMESTAMPTZ DEFAULT NOW(),
      attempts     INTEGER     DEFAULT 0,
      status       TEXT        DEFAULT 'pending',
      error        TEXT
    );
  `)

  const { rows } = await db.query(
    `SELECT value FROM _meta WHERE key = 'schema_version'`
  )
  const currentVersion = rows[0] ? parseInt(rows[0].value, 10) : 0

  if (currentVersion < 1) {
    await _migration_v1(db)
  }
  if (currentVersion < 2) {
    await _migration_v2(db)
  }
  if (currentVersion < 3) {
    await _migration_v3(db)
  }

  if (currentVersion < SCHEMA_VERSION) {
    await db.query(
      `INSERT INTO _meta (key, value) VALUES ('schema_version', $1)
       ON CONFLICT (key) DO UPDATE SET value = EXCLUDED.value`,
      [String(SCHEMA_VERSION)]
    )
    console.info('[localDb] migrated to schema v' + SCHEMA_VERSION)
  }
}

// ─── v1 schema — one table per sync target ───────────────────────────────────
//
// Pattern: id + updated_at as indexed top-level columns for sync diffing,
// a handful of table-specific columns for common WHERE/ORDER BY, and _data
// JSONB holding the complete Supabase row so no field is ever inaccessible.

async function _migration_v1(db) {
  // Build CREATE TABLE statements dynamically from syncConfig so the schema
  // stays in sync with the table list without manual duplication.
  for (const tableConfig of SYNC_TABLES) {
    const { name, indexedCols = [] } = tableConfig

    // Determine column types for well-known column names.
    const colDefs = indexedCols.map(col => `${col} ${_pgType(col)}`).join(',\n      ')

    await db.exec(`
      CREATE TABLE IF NOT EXISTS ${name} (
        id         TEXT PRIMARY KEY,
        updated_at TIMESTAMPTZ,
        ${colDefs ? colDefs + ',' : ''}
        _data      JSONB NOT NULL
      );
      CREATE INDEX IF NOT EXISTS ${name}_updated_at ON ${name}(updated_at);
    `)

    // Extra indexes per table for common query patterns.
    for (const col of indexedCols) {
      if (['id', 'updated_at', '_data'].includes(col)) continue
      await db.exec(
        `CREATE INDEX IF NOT EXISTS ${name}_${col} ON ${name}(${col});`
      )
    }
  }
}

// ─── v2 schema — document_drafts added to SYNC_TABLES after v1 shipped ───────
//
// New users get this table via _migration_v1 (which reads SYNC_TABLES).
// Existing users who already ran v1 need this explicit CREATE TABLE.
// All statements are IF NOT EXISTS so re-running is safe.

async function _migration_v2(db) {
  await db.exec(`
    CREATE TABLE IF NOT EXISTS document_drafts (
      id          TEXT PRIMARY KEY,
      updated_at  TIMESTAMPTZ,
      title       TEXT,
      status      TEXT,
      project_id  TEXT,
      property_id TEXT,
      company_id  TEXT,
      _data       JSONB NOT NULL
    );
    CREATE INDEX IF NOT EXISTS document_drafts_updated_at  ON document_drafts(updated_at);
    CREATE INDEX IF NOT EXISTS document_drafts_title       ON document_drafts(title);
    CREATE INDEX IF NOT EXISTS document_drafts_status      ON document_drafts(status);
    CREATE INDEX IF NOT EXISTS document_drafts_project_id  ON document_drafts(project_id);
    CREATE INDEX IF NOT EXISTS document_drafts_property_id ON document_drafts(property_id);
    CREATE INDEX IF NOT EXISTS document_drafts_company_id  ON document_drafts(company_id);
  `)
}

// ─── v3 schema — email_intelligence + saved_emails added to SYNC_TABLES ──────
//
// Makes the inbox offline-first: a hung/timed-out Supabase query falls through
// to these locally-mirrored rows instead of an empty list. New users get the
// tables via _migration_v1 (reads SYNC_TABLES); existing users need this
// explicit CREATE. All statements are IF NOT EXISTS so re-running is safe.

async function _migration_v3(db) {
  await db.exec(`
    CREATE TABLE IF NOT EXISTS email_intelligence (
      id               TEXT PRIMARY KEY,
      updated_at       TIMESTAMPTZ,
      user_id          TEXT,
      category         TEXT,
      status           TEXT,
      urgency          TEXT,
      email_type       TEXT,
      triage_status    TEXT,
      is_read          BOOLEAN,
      is_outbound      BOOLEAN,
      is_starred       BOOLEAN,
      sent_at          TIMESTAMPTZ,
      gmail_thread_id  TEXT,
      gmail_message_id TEXT,
      _data            JSONB NOT NULL
    );
    CREATE INDEX IF NOT EXISTS email_intelligence_updated_at       ON email_intelligence(updated_at);
    CREATE INDEX IF NOT EXISTS email_intelligence_user_id          ON email_intelligence(user_id);
    CREATE INDEX IF NOT EXISTS email_intelligence_category         ON email_intelligence(category);
    CREATE INDEX IF NOT EXISTS email_intelligence_status           ON email_intelligence(status);
    CREATE INDEX IF NOT EXISTS email_intelligence_sent_at          ON email_intelligence(sent_at);
    CREATE INDEX IF NOT EXISTS email_intelligence_gmail_thread_id  ON email_intelligence(gmail_thread_id);
    CREATE INDEX IF NOT EXISTS email_intelligence_gmail_message_id ON email_intelligence(gmail_message_id);

    CREATE TABLE IF NOT EXISTS saved_emails (
      id               TEXT PRIMARY KEY,
      updated_at       TIMESTAMPTZ,
      gmail_message_id TEXT,
      gmail_thread_id  TEXT,
      sender_email     TEXT,
      is_outbound      BOOLEAN,
      sent_at          TIMESTAMPTZ,
      user_id          TEXT,
      _data            JSONB NOT NULL
    );
    CREATE INDEX IF NOT EXISTS saved_emails_updated_at       ON saved_emails(updated_at);
    CREATE INDEX IF NOT EXISTS saved_emails_gmail_message_id ON saved_emails(gmail_message_id);
    CREATE INDEX IF NOT EXISTS saved_emails_gmail_thread_id  ON saved_emails(gmail_thread_id);
    CREATE INDEX IF NOT EXISTS saved_emails_sent_at          ON saved_emails(sent_at);
  `)
}

// Map well-known column names to Postgres types.
function _pgType(col) {
  if (col === 'priority_tier') return 'INTEGER'
  if (['is_client', 'is_read', 'is_outbound', 'is_starred'].includes(col)) return 'BOOLEAN'
  if (['due_date', 'contact_date', 'sent_at'].includes(col)) return 'TIMESTAMPTZ'
  if (col.endsWith('_id'))     return 'TEXT'
  return 'TEXT'
}

// ─── Sync state helpers (used by syncEngine) ─────────────────────────────────

export async function getSyncState(tableName) {
  if (!_db) return null
  const { rows } = await _db.query(
    `SELECT last_synced_at, row_count FROM _sync_state WHERE table_name = $1`,
    [tableName]
  )
  return rows[0] || null
}

export async function setSyncState(tableName, { lastSyncedAt, rowCount, error = null }) {
  if (!_db) return
  await _db.query(
    `INSERT INTO _sync_state (table_name, last_synced_at, row_count, sync_error)
     VALUES ($1, $2, $3, $4)
     ON CONFLICT (table_name) DO UPDATE
       SET last_synced_at = EXCLUDED.last_synced_at,
           row_count      = EXCLUDED.row_count,
           sync_error     = EXCLUDED.sync_error`,
    [tableName, lastSyncedAt, rowCount ?? 0, error]
  )
}

// ─── Mutation queue helpers (used by syncEngine Phase 3) ─────────────────────

export async function enqueueMutation({ tableName, operation, rowId, payload }) {
  if (!_db) return
  await _db.query(
    `INSERT INTO _mutation_queue (table_name, operation, row_id, payload)
     VALUES ($1, $2, $3, $4)`,
    [tableName, operation, rowId ?? null, payload ? JSON.stringify(payload) : null]
  )
}

export async function getPendingMutations(limit = 50) {
  if (!_db) return []
  const { rows } = await _db.query(
    `SELECT * FROM _mutation_queue
     WHERE status = 'pending' AND attempts < 5
     ORDER BY created_at ASC
     LIMIT $1`,
    [limit]
  )
  return rows
}

export async function markMutationDone(id) {
  if (!_db) return
  await _db.query(
    `UPDATE _mutation_queue SET status = 'done' WHERE id = $1`, [id]
  )
}

export async function markMutationFailed(id, error) {
  if (!_db) return
  await _db.query(
    `UPDATE _mutation_queue
     SET status = 'failed', attempts = attempts + 1, error = $2
     WHERE id = $1`,
    [id, String(error)]
  )
}
